import { Select, SelectOption, SelectOptions } from '@pay-com/components'
import {
  Controller,
  FieldPath,
  FieldValues,
  UseFormReturn,
  useFormContext
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getErrorMessage } from 'utils/get-error-message'

interface ControlledMultiSelectProps<T extends FieldValues> {
  name: FieldPath<T>
  label?: string
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  className?: string
  onChange?: (values: string[], methods: UseFormReturn<T>) => void
}

export const ControlledMultiSelect = <T extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  disabled,
  isLoading,
  className,
  onChange
}: ControlledMultiSelectProps<T>) => {
  const methods = useFormContext<T>()
  const { control } = methods
  const { t } = useTranslation('errors')
  const portalId = `${name.replaceAll('.', '-').replaceAll(':', '-')}-multi-portal`
  const optionsMap = Object.fromEntries(options.map(opt => [opt.value, opt]))

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState
        const currentValue =
          field.value?.map(
            (val: string) => optionsMap[val] ?? { value: val, label: val }
          ) ?? []

        return (
          <div>
            <Select
              isMulti
              value={currentValue}
              options={options}
              onChange={(selected: SelectOptions | null) => {
                const values =
                  selected?.map((opt: SelectOption) => opt.value) ?? []
                field.onChange(values)
                onChange?.(values, methods)
              }}
              onBlur={field.onBlur}
              portalOptions={{
                portalSelector: `#${portalId}`,
                zIndex: 1000
              }}
              label={label}
              placeholder={placeholder}
              disabled={disabled || isLoading}
              error={Boolean(error)}
              errorMessage={getErrorMessage(t, error?.message)}
              size="medium"
              className={className}
              menuPosition="fixed"
              menuShouldBlockScroll
            />
            <div id={portalId} />
          </div>
        )
      }}
    />
  )
}
